/**
 * Fiscal Period Context
 * Provides global fiscal period settings and date range across the app
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  getGlobalSettings,
  getCurrentRange,
  updateGlobalSettings as updateSettingsAPI
} from '../api/fiscalPeriods';

const FiscalPeriodContext = createContext(null);

export const useFiscalPeriod = () => {
  const context = useContext(FiscalPeriodContext);
  if (!context) {
    console.warn('useFiscalPeriod must be used within FiscalPeriodProvider');
    // Return safe defaults
    return {
      settings: { period_type: 'forever' },
      periodRange: { start_date: null, end_date: null, period_label: 'All Time' },
      loading: false,
      error: null,
      refresh: () => {},
      updateSettings: () => {},
      getDateRangeParams: () => ({})
    };
  }
  return context;
};

export const FiscalPeriodProvider = ({ children, module = 'retail' }) => {
  const [settings, setSettings] = useState({
    period_type: 'forever',
    custom_start_date: null,
    custom_end_date: null,
    fiscal_year_start_month: 1,
    fiscal_year_start_day: 1,
    show_period_badge_on_dashboard: true,
    allow_users_to_override: true,
    module: module
  });

  const [periodRange, setPeriodRange] = useState({
    start_date: null,
    end_date: null,
    period_type: 'forever',
    period_label: 'All Time'
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentModule] = useState(module);

  // Fetch settings and current range
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [settingsRes, rangeRes] = await Promise.all([
        getGlobalSettings(currentModule),
        getCurrentRange(currentModule)
      ]);

      if (settingsRes.success) {
        setSettings(settingsRes.data);
      }

      if (rangeRes.success) {
        setPeriodRange(rangeRes.data);
      }
    } catch (err) {
      console.error('Error fetching fiscal period data:', err);
      setError(err.message);
      // Keep defaults on error
    } finally {
      setLoading(false);
    }
  }, [currentModule]);

  // Initial fetch - skip on public pages and when not authenticated to avoid auth issues
  useEffect(() => {
    const publicPaths = ['/login', '/forgot-password', '/reset-password', '/demo-login'];
    const isPublicPage = publicPaths.some((path) => window.location.pathname.startsWith(path));
    const hasAuthToken = !!localStorage.getItem('accessToken');

    if (!isPublicPage && hasAuthToken) {
      fetchData();
    } else {
      // Use defaults for public pages or unauthenticated users
      setLoading(false);
    }
  }, [currentModule]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update settings
  const updateSettings = async (newSettings) => {
    try {
      setLoading(true);
      const response = await updateSettingsAPI(newSettings, currentModule);

      if (response.success) {
        setSettings(response.data);
        // Refresh the period range after settings change
        const rangeRes = await getCurrentRange(currentModule);
        if (rangeRes.success) {
          setPeriodRange(rangeRes.data);
        }
        return { success: true };
      }

      return { success: false, message: response.message };
    } catch (err) {
      console.error('Error updating fiscal settings:', err);
      return { success: false, message: err.response?.data?.message || err.message };
    } finally {
      setLoading(false);
    }
  };

  // Get date range params for API calls
  const getDateRangeParams = useCallback(
    (overrideRange = null) => {
      const range = overrideRange || periodRange;

      // If forever or no dates, return object with bypass flag
      if (range.period_type === 'forever' || (!range.start_date && !range.end_date)) {
        return { ignore_global_period: true };
      }

      const params = {};
      if (range.start_date) {
        params.start_date = range.start_date;
        params.startDate = range.start_date; // Some endpoints use camelCase
      }
      if (range.end_date) {
        params.end_date = range.end_date;
        params.endDate = range.end_date;
      }

      return params;
    },
    [periodRange]
  );

  // Get SQL-ready date conditions
  const getDateCondition = useCallback(
    (dateField = 'created_at', paramOffset = 1) => {
      if (
        periodRange.period_type === 'forever' ||
        (!periodRange.start_date && !periodRange.end_date)
      ) {
        return { condition: '', params: [], nextParam: paramOffset };
      }

      let condition = '';
      const params = [];
      let paramNum = paramOffset;

      if (periodRange.start_date) {
        condition += ` AND ${dateField} >= $${paramNum}`;
        params.push(periodRange.start_date);
        paramNum++;
      }

      if (periodRange.end_date) {
        condition += ` AND ${dateField} <= $${paramNum}`;
        params.push(periodRange.end_date);
        paramNum++;
      }

      return { condition, params, nextParam: paramNum };
    },
    [periodRange]
  );

  const value = {
    settings,
    periodRange,
    loading,
    error,
    refresh: fetchData,
    updateSettings,
    getDateRangeParams,
    getDateCondition,
    // Convenience getters
    isAllTime: periodRange.period_type === 'forever',
    periodLabel: periodRange.period_label,
    startDate: periodRange.start_date,
    endDate: periodRange.end_date,
    module: currentModule
  };

  return <FiscalPeriodContext.Provider value={value}>{children}</FiscalPeriodContext.Provider>;
};

export default FiscalPeriodContext;
